import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/AddOutlined';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import SearchableSelect from '@/components/common/SearchableSelect';
import StatusChip from '@/components/common/StatusChip';
import { IfPermitted } from '@/components/auth/RequirePermission';
import { useDateFormat } from '@/hooks/useDateFormat';
import { useProcessConfiguration } from '@/hooks/useProcessConfiguration';
import TransferSourcePicker from './TransferSourcePicker';
import type { TransferFormLine } from './TransferSourcePicker';
import { PERMISSIONS } from '@/types/auth';
import type { WarehouseDto, LocationDto } from '@/types/organization';
import type { MaterialDto, PagedResult } from '@/types/materials';
import type { CreateStockTransferRequest, StockTransferDto, StockTransferLineInput, UpdateStockTransferRequest } from '@/types/adjustments';

// The form line shape lives with the picker that produces it.
type FormLine = TransferFormLine;

const emptyLine: FormLine = { materialId: '', qty: 0, fromLocationId: null, toLocationId: null, batchId: null, batchNo: null, clientName: null };

export default function StockTransfersPage() {
  const { t } = useTranslation('stockTransfer');
  const { formatDate } = useDateFormat();
  const queryClient = useQueryClient();

  // Same gate as Stock Adjustment: scanning is only offered where Barcode Required is on.
  const { data: processConfig } = useProcessConfiguration();
  const barcodeMandatory = processConfig?.barcodeMandatory ?? false;

  const { data: transfers = [], isLoading } = useQuery({
    queryKey: ['stock-transfers'],
    queryFn: async () => (await apiClient.get<StockTransferDto[]>('/stock-transfers')).data,
  });
  const { data: warehouses = [] } = useQuery({
    queryKey: ['warehouses'],
    queryFn: async () => (await apiClient.get<WarehouseDto[]>('/warehouses')).data,
  });
  const { data: materialsPage } = useQuery({
    queryKey: ['materials', 'all-for-picker'],
    queryFn: async () => (await apiClient.get<PagedResult<MaterialDto>>('/materials', { params: { page: 1, pageSize: 200 } })).data,
  });
  const materials = materialsPage?.items ?? [];

  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [fromWarehouseId, setFromWarehouseId] = useState('');
  const [toWarehouseId, setToWarehouseId] = useState('');
  const [lines, setLines] = useState<FormLine[]>([{ ...emptyLine }]);
  const [scanError, setScanError] = useState<string | null>(null);

  // The FROM side's zone/location lists live in TransferSourcePicker, which owns that whole area.
  // Only the destination list is still needed here, to pass down.
  const { data: toLocations = [] } = useQuery({
    queryKey: ['locations', toWarehouseId],
    queryFn: async () => (await apiClient.get<LocationDto[]>('/locations', { params: { warehouseId: toWarehouseId } })).data,
    enabled: Boolean(toWarehouseId),
  });

  const resetForm = () => {
    setEditingId(null);
    setFromWarehouseId('');
    setToWarehouseId('');
    setLines([{ ...emptyLine }]);
  };

  // Only usable while Status is still "Draft" — mirrors StockTransferService.UpdateAsync's guard.
  const openEdit = (r: StockTransferDto) => {
    setEditingId(r.id);
    setFromWarehouseId(r.fromWarehouseId);
    setToWarehouseId(r.toWarehouseId);
    setLines(r.lines.map((l) => ({
      materialId: l.materialId, qty: l.qty, fromLocationId: l.fromLocationId, toLocationId: l.toLocationId,
      batchId: l.batchId, batchNo: l.batchNo, clientName: l.clientName,
    })));
    setOpen(true);
  };

  const createMutation = useMutation({
    mutationFn: () => {
      const cleanLines: StockTransferLineInput[] = lines
        // Barcode-wise a line is a scanned unit; quantity-wise it needs an actual quantity - the
        // picker seeds rows lazily, so a row the operator never typed into must not be submitted.
        .filter((l) => (l.scannedBarcode ? true : Boolean(l.materialId) && l.qty > 0))
        .map(({
          batchNo: _batchNo, clientName: _clientName, materialCode: _materialCode,
          fromLocationCode: _fromLocationCode, availableQty: _availableQty, ...line
        }) => line);
      if (editingId) {
        const payload: UpdateStockTransferRequest = { fromWarehouseId, toWarehouseId, lines: cleanLines };
        return apiClient.put(`/stock-transfers/${editingId}`, payload);
      }
      const payload: CreateStockTransferRequest = { fromWarehouseId, toWarehouseId, lines: cleanLines };
      return apiClient.post('/stock-transfers', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stock-transfers'] });
      setOpen(false);
    },
  });

  const completeMutation = useMutation({
    mutationFn: (id: string) => apiClient.post(`/stock-transfers/${id}/complete`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['stock-transfers'] }),
  });

  const columns: DataTableColumn<StockTransferDto>[] = [
    { field: 'transferNumber', headerName: t('table.number') },
    { field: 'fromWarehouseName', headerName: t('table.from'), filterable: true },
    { field: 'toWarehouseName', headerName: t('table.to'), filterable: true },
    { field: 'transferDate', headerName: t('table.date'), render: (r) => formatDate(r.transferDate) },
    { field: 'lines', headerName: t('table.lines'), render: (r) => r.lines.length },
    { field: 'status', headerName: t('table.status'), render: (r) => <StatusChip status={r.status} />, filterable: true },
    {
      field: 'action', headerName: '', render: (r) => (
        <IfPermitted permission={PERMISSIONS.STOCK_TRANSFER_MANAGE}>
          <Stack direction="row" spacing={1}>
            {r.status === 'Draft' && (
              <Button size="small" onClick={() => openEdit(r)}>
                {t('edit')}
              </Button>
            )}
            {r.status !== 'Completed' && (
              <Button size="small" variant="outlined" disabled={completeMutation.isPending} onClick={() => completeMutation.mutate(r.id)}>
                {t('complete')}
              </Button>
            )}
          </Stack>
        </IfPermitted>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        actions={
          <IfPermitted permission={PERMISSIONS.STOCK_TRANSFER_MANAGE}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => { resetForm(); setOpen(true); }}>
              {t('add')}
            </Button>
          </IfPermitted>
        }
      />

      <DataTable columns={columns} rows={transfers} loading={isLoading} searchable getRowId={(r) => r.id} exportFileName="stock-transfers" />

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingId ? t('edit') : t('add')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={2}>
              <SearchableSelect
                label={t('form.fromWarehouse')} value={fromWarehouseId} onChange={setFromWarehouseId} fullWidth
                options={warehouses.map((w) => ({ value: w.id, label: w.name }))}
              />
              <SearchableSelect
                label={t('form.toWarehouse')} value={toWarehouseId} onChange={setToWarehouseId} fullWidth
                options={warehouses.map((w) => ({ value: w.id, label: w.name }))}
              />
            </Stack>

            {/* The whole source side — area (warehouse → zone → rack/bin), then either the
                quantity-wise item list or the barcode-wise unit picker depending on Process
                Configuration. Destination location is chosen per row inside. */}
            {fromWarehouseId ? (
              <TransferSourcePicker
                fromWarehouseId={fromWarehouseId}
                barcodeMandatory={barcodeMandatory}
                materials={materials}
                toLocations={toLocations}
                lines={lines}
                setLines={setLines}
                error={scanError}
                setError={setScanError}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">{t('form.selectFromWarehouse')}</Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)}>{t('cancel')}</Button>
          <Button
            variant="contained"
            disabled={!fromWarehouseId || !toWarehouseId || lines.some((l) => !l.materialId || l.qty <= 0) || createMutation.isPending}
            onClick={() => createMutation.mutate()}
          >
            {t('save')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
